import React, { useEffect, useRef, useState } from 'react';
import type { BaseInputWrapperProps } from '../base-input-wrapper/base-input-wrapper';
import { BaseInputWrapper } from '../base-input-wrapper/base-input-wrapper';
import styles from './text-box.module.scss';
import { classNames } from 'tradera/utils/class-names';
import { Button } from 'tradera/components/atoms/button/button';
import CloseNarrowIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';

export type TextBoxProps = {
    /**
     * If true, the height of the textarea will be automatically adjusted to fit the content.
     * Defaults to true.
     */
    autoHeight?: boolean;
    hidePlaceholderOnFocus?: boolean;
    showClearButton?: boolean;
    onClearButtonClick?: () => void;
    emptyStateIconRight?: React.ReactNode;
} & Omit<BaseInputWrapperProps, 'children' | 'isPristine'> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextBox = React.forwardRef<HTMLTextAreaElement, TextBoxProps>(
    function TextAreaInput(
        {
            value,
            label,
            hint,
            error,
            help,
            onChange,
            onClearButtonClick,
            emptyStateIconRight,
            showClearButton,
            name,
            collapsed,
            className,
            style,
            hidePlaceholderOnFocus,
            autoHeight = true,
            supressErrorWhenPristine = false,
            ...props
        },
        ref,
    ) {
        const [localValue, setLocalValue] = useState<string>(
            value?.toString() || '',
        );
        const [isPristine, setIsPristine] = useState(true);
        const localRef = useRef<HTMLTextAreaElement | null>(null);

        const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (props.readOnly || props.disabled) return;
            /**
             * Set local value, needed if the component is not controlled.
             */
            setLocalValue(e.target.value);
            setIsPristine(false);

            onChange?.(e);
        };

        const handleOnClear = () => {
            if (props.readOnly || props.disabled) return;
            /**
             * Set local value, needed if the component is not controlled.
             */
            setLocalValue('');

            onClearButtonClick?.();
        };

        useEffect(() => {
            if (!localRef.current || !autoHeight) return;
            localRef.current.style.height = 'auto';
            if (value || localValue) {
                localRef.current.style.height = `${localRef.current.scrollHeight}px`;
            }
        }, [localValue, value, autoHeight]);

        const supressError = supressErrorWhenPristine && isPristine;

        return (
            <BaseInputWrapper
                label={label}
                hint={hint}
                error={error}
                help={help}
                name={name}
                isPristine={isPristine}
                supressErrorWhenPristine={supressErrorWhenPristine}
                // so that we can add mt-1 and such to the wrapper
                className={classNames(className, {
                    [styles.disabled]: props.disabled || props.readOnly,
                })}
                style={style}
                collapsed={collapsed}>
                <div
                    className={classNames(styles.textBoxWrapper, {
                        [styles.error]: !!error && !supressError,
                    })}>
                    <textarea
                        className={classNames(styles.textArea, {
                            [styles.hidePlaceholderOnFocus]:
                                hidePlaceholderOnFocus,
                        })}
                        value={
                            typeof value === 'undefined' ? localValue : value
                        }
                        onChange={handleOnChange}
                        ref={(node) => {
                            if (typeof ref === 'function') {
                                ref(node);
                            } else if (ref) {
                                ref.current = node;
                            }

                            localRef.current = node;
                        }}
                        id={name}
                        name={name}
                        {...props}
                    />
                    {!localValue &&
                        !value &&
                        emptyStateIconRight &&
                        emptyStateIconRight}
                    {showClearButton &&
                        (typeof value === 'undefined' ? localValue : value) && (
                            <Button
                                theme="unbutton"
                                className="align-self-start mt-1"
                                onClick={handleOnClear}
                                icon={
                                    <CloseNarrowIcon className="icon icon-xs" />
                                }
                            />
                        )}
                </div>
            </BaseInputWrapper>
        );
    },
);
